import * as React from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface DateOfBirthPickerProps {
  date?: Date;
  onDateChange: (date: Date | undefined) => void;
  placeholder?: string;
  className?: string;
  type?: "dob" | "appointment";
}

export function DateOfBirthPicker({
  date,
  onDateChange,
  placeholder = "Pick a date",
  className,
  type = "dob"
}: DateOfBirthPickerProps) {
  const [selectedYear, setSelectedYear] = React.useState<string>(
    date ? date.getFullYear().toString() : ''
  );
  const [selectedMonth, setSelectedMonth] = React.useState<string>(
    date ? (date.getMonth() + 1).toString().padStart(2, '0') : ''
  );
  const [selectedDay, setSelectedDay] = React.useState<string>(
    date ? date.getDate().toString().padStart(2, '0') : ''
  );

  // Generate year options
  const currentYear = new Date().getFullYear();
  const startYear = type === "dob" ? 1900 : 1990;
  const endYear = type === "dob" ? currentYear : 2050;
  const yearOptions = Array.from({ length: endYear - startYear + 1 }, (_, i) => endYear - i);
  
  // Month options
  const monthOptions = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  // Generate day options (1-31)
  const dayOptions = Array.from({ length: 31 }, (_, i) => (i + 1).toString().padStart(2, '0'));

  // Handle year selection
  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    updateDate(year, selectedMonth, selectedDay);
  };

  // Handle month selection
  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
    updateDate(selectedYear, month, selectedDay);
  };

  // Handle day selection
  const handleDayChange = (day: string) => {
    setSelectedDay(day);
    updateDate(selectedYear, selectedMonth, day);
  };

  // Update date when any dropdown changes
  const updateDate = (year: string, month: string, day: string) => {
    if (year && month && day) {
      const newDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      onDateChange(newDate);
    } else if (!year && !month && !day) {
      onDateChange(undefined);
    }
  };

  const displayText = React.useMemo(() => {
    if (selectedYear && selectedMonth && selectedDay) {
      const monthName = monthOptions.find(m => m.value === selectedMonth)?.label;
      return `${monthName} ${selectedDay}, ${selectedYear}`;
    }
    return placeholder;
  }, [selectedYear, selectedMonth, selectedDay, placeholder]);

  return (
    <div className={cn("flex items-center space-x-2", className)}>
      <CalendarIcon className="h-4 w-4 text-gray-500" />
      
      <Select value={selectedMonth} onValueChange={handleMonthChange}>
        <SelectTrigger className="w-32 bg-white border-gray-300 z-10">
          <SelectValue placeholder="Month" />
        </SelectTrigger>
        <SelectContent className="bg-white border shadow-lg z-20">
          {monthOptions.map((month) => (
            <SelectItem key={month.value} value={month.value}>
              {month.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      
      <Select value={selectedDay} onValueChange={handleDayChange}>
        <SelectTrigger className="w-20 bg-white border-gray-300 z-10">
          <SelectValue placeholder="Day" />
        </SelectTrigger>
        <SelectContent className="bg-white border shadow-lg z-20 max-h-60">
          {dayOptions.map((day) => (
            <SelectItem key={day} value={day}>
              {day}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      
      <Select value={selectedYear} onValueChange={handleYearChange}>
        <SelectTrigger className="w-24 bg-white border-gray-300 z-10">
          <SelectValue placeholder="Year" />
        </SelectTrigger>
        <SelectContent className="bg-white border shadow-lg z-20 max-h-60">
          {yearOptions.map((year) => (
            <SelectItem key={year} value={year.toString()}>
              {year}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}